import { useCallback, useEffect, useRef, useState } from 'react';
import { getLiveKitToken } from '@/api/chat';
import { Room, RoomEvent } from 'livekit-client';
import { acquireScreenWakeLock, releaseScreenWakeLock } from '@/utils/screenWakeLock';
import {
  configureRemoteVoiceTrack,
  applyVoiceTransportPreferences,
  getVoiceTransportSnapshot,
} from '@/utils/realtimeVoiceTransport';

const AUDIO_CAPTURE_DEFAULTS = {
  autoGainControl: true,
  echoCancellation: true,
  noiseSuppression: true,
  channelCount: 1,
};

export const useRealtimeVoiceAssistant = ({ context = {}, onEvent, onToolCall, onLessonEvent } = {}) => {
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [transcript, setTranscript] = useState('');
  const [assistantText, setAssistantText] = useState('');
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [roomName, setRoomName] = useState('');

  const roomRef = useRef(null);
  const activeRef = useRef(false);
  const userInitiatedEndRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const lastCustomRoomNameRef = useRef(null);
  const startTimeRef = useRef(0);
  const attachedAudioElementsRef = useRef(new Set());
  const thinkingTimeoutRef = useRef(null);

  const cleanup = useCallback((keepActiveState = false) => {
    if (!keepActiveState) {
      activeRef.current = false;
      startTimeRef.current = 0;
      reconnectAttemptsRef.current = 0;
    }
    releaseScreenWakeLock('ai-tutor');

    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = null;
    }

    // Explicitly detach and remove any dynamically attached audio elements to prevent background audio leaks
    if (attachedAudioElementsRef.current) {
      attachedAudioElementsRef.current.forEach((el) => {
        try {
          el.pause();
          el.srcObject = null;
          el.remove();
        } catch (e) {
          // ignore
        }
      });
      attachedAudioElementsRef.current.clear();
    }

    if (roomRef.current) {
      try {
        roomRef.current.disconnect();
      } catch (e) {
        console.error('Error disconnecting LiveKit room:', e);
      }
      roomRef.current = null;
    }
    setIsMuted(false);
  }, []);

  const start = useCallback(async (customRoomName, isAutoReconnect = false) => {
    if (activeRef.current && !isAutoReconnect) return;

    userInitiatedEndRef.current = false;
    lastCustomRoomNameRef.current = customRoomName;
    if (!isAutoReconnect) {
      reconnectAttemptsRef.current = 0;
      setTranscript('');
      setAssistantText('');
      setMessages([]);
    }
    setIsOpen(true);
    setError('');
    setState(isAutoReconnect ? 'reconnecting' : 'connecting');
    activeRef.current = true;
    if (!isAutoReconnect || !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }

    try {
      // 1. Generate room name and get token
      const tutorId = context.tutor_id || context.tutorId || 'default';
      const isStagingEnv = typeof window !== 'undefined' && (
        window.location.hostname.includes('staging') ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      );
      const envPrefix = isStagingEnv ? 'staging-' : '';
      const validCustomName = (typeof customRoomName === 'string' && customRoomName.trim() && !customRoomName.includes('[object'))
        ? customRoomName.trim()
        : null;
      const generatedRoomName = validCustomName || `call-tutor-${envPrefix}${tutorId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setRoomName(generatedRoomName);
      const { token, url, voice_transport, room_name: resolvedRoomName } = await getLiveKitToken(generatedRoomName, context);
      if (resolvedRoomName && resolvedRoomName !== generatedRoomName) {
        setRoomName(resolvedRoomName);
      }
      
      if (!token) {
        throw new Error('Failed to retrieve LiveKit token.');
      }

      // 2. Initialize Room
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: AUDIO_CAPTURE_DEFAULTS,
      });
      roomRef.current = room;

      // Force WebRTC PeerConnection to route through authenticated TURN/TLS over TCP (port 443)
      // when turn-tls is requested. This directly eliminates the 21-25% UDP packet drops.
      applyVoiceTransportPreferences(room, voice_transport);

      // 3. Set up event handlers
      room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        try {
          if (track.kind === 'audio') {
            // Keep a small receiver-side jitter cushion. Unlike buffering model
            // PCM on the server, this can absorb packets that arrive late on
            // the actual LiveKit/WebRTC downlink before Chrome has to conceal
            // missing speech samples.
            configureRemoteVoiceTrack(track);
            const logTransportSnapshot = async () => {
              for (let attempt = 0; attempt < 6; attempt++) {
                const snapshot = await getVoiceTransportSnapshot(track);
                if (snapshot?.candidateType) {
                  console.info('LiveKit remote voice transport snapshot:', snapshot);
                  return;
                }
                await new Promise((resolve) => setTimeout(resolve, 250));
              }
            };
            logTransportSnapshot();

            // Detach through LiveKit so its internal attachment registry stays
            // in sync and duplicate elements cannot play out of phase.
            const existingEls = track.detach();
            existingEls.forEach((el) => {
              try {
                attachedAudioElementsRef.current.delete(el);
                el.pause();
                el.srcObject = null;
                el.remove();
              } catch (_) {}
            });

            const audioEl = track.attach();
            audioEl.autoplay = true;
            audioEl.playsInline = true;
            audioEl.style.display = 'none';
            document.body.appendChild(audioEl);
            attachedAudioElementsRef.current.add(audioEl);

            // Ensure browser AudioContext is running and active
            room.startAudio().catch((e) => console.warn('room.startAudio warning:', e));
            setState('live');
          }
        } catch (err) {
          console.error('Error handling track subscribed:', err);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        try {
          const detachedEls = track.detach();
          if (Array.isArray(detachedEls)) {
            detachedEls.forEach((el) => {
              attachedAudioElementsRef.current.delete(el);
              try {
                el.pause();
                el.srcObject = null;
                el.remove();
              } catch (_) {}
            });
          }
        } catch (err) {
          console.error('Error handling track unsubscribed:', err);
        }
      });

      room.on(RoomEvent.TranscriptionReceived, (transcriptionOrSegments, participant) => {
        try {
          const segments = Array.isArray(transcriptionOrSegments)
            ? transcriptionOrSegments
            : (transcriptionOrSegments?.segments || []);

          const sender = participant === room.localParticipant ? 'user' : 'tutor';
          segments.forEach((seg) => {
            const text = seg.text || '';
            const segmentId = seg.id;
            const isFinal = seg.final || seg.isFinal || false;

            // Filter out Whisper silence hallucinations and acoustic echoes
            if (sender === 'user') {
              // 1. Drop foreign script hallucinations (Korean Hangul, Cyrillic, Chinese)
              if (/[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F\u0400-\u04FF\u4E00-\u9FFF]/.test(text)) {
                return;
              }
              const lower = text.toLowerCase().trim();
              if (
                /\b(?:https?:\/\/|www\.)\S+/i.test(lower) ||
                /\b[a-z0-9_\-\.]+\.(?:gov|com|org|edu|net|io)\b/i.test(lower) ||
                lower.includes('learn more at') ||
                lower.includes('for more information') ||
                lower.includes('visit our website') ||
                lower.includes('visit website') ||
                lower.includes('all rights reserved') ||
                lower.includes('see you next time') ||
                lower.includes('subscribe') ||
                lower.includes('mbc') ||
                lower.includes('subtitles by') ||
                lower.includes('thank you for watching') ||
                lower.includes('thanks for watching') ||
                lower.includes('amara.org') ||
                lower.includes('opensubtitles') ||
                lower.includes('thank you. thank you') ||
                lower.includes('thank you thank you') ||
                lower.includes('questions and answers session') ||
                lower.includes('session for cbse students')
              ) {
                return;
              }
              // 2. Drop greeting echoes within the first 12 seconds
              if (Date.now() - startTimeRef.current < 12000) {
                const echoKeywords = [
                  'விரும்புகிறீர்',
                  'படிக்க விரும்ப',
                  'என்ன படிக்க',
                  'what would you like to learn',
                  'how can i help',
                  'வணக்கம்',
                  'your tutor',
                ];
                if (echoKeywords.some(kw => lower.includes(kw))) {
                  return;
                }
              }
            }

            if (participant === room.localParticipant) {
              setTranscript(text);
            } else {
              setAssistantText(text);
            }

            const finalId = segmentId || (sender === 'user' ? 'user-latest' : 'tutor-latest');

            setMessages((prev) => {
              const trimmed = text.trim();
              if (!trimmed || trimmed.length <= 1 || ['...', '.', '…', ',', '-', '—'].includes(trimmed)) return prev;
              
              const existingIdx = prev.findIndex((m) => m.id === finalId && m.sender === sender);
              let updated = [...prev];

              if (existingIdx !== -1) {
                updated[existingIdx] = {
                  ...updated[existingIdx],
                  text,
                  isFinal,
                };
                return updated;
              }

              // Check if new message text is a substring of a recent existing message (within last 15s)
              const now = Date.now();
              const isSubstring = prev.some((m) => 
                m.sender === sender && 
                now - m.timestamp < 15000 && 
                (m.text || '').includes(text)
              );
              if (isSubstring) {
                return prev;
              }

              // Check if new message is a super-string of recent existing messages (within last 15s)
              const cleanText = text.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g,"").replace(/\s+/g, " ").toLowerCase();
              const duplicateIndices = [];
              
              prev.forEach((m, idx) => {
                if (m.sender === sender && now - m.timestamp < 15000) {
                  const mClean = (m.text || '').replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g,"").replace(/\s+/g, " ").toLowerCase();
                  if (cleanText.includes(mClean) && mClean.length > 5) {
                    duplicateIndices.push(idx);
                  }
                }
              });

              if (duplicateIndices.length > 0) {
                updated = prev.filter((_, idx) => !duplicateIndices.includes(idx));
              }

              updated.push({
                id: finalId,
                sender,
                text,
                isFinal,
                timestamp: now,
              });

              return updated;
            });
          });
        } catch (err) {
          console.error('Error handling transcription received:', err);
        }
      });

      room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
        try {
          const text = typeof payload === 'string'
            ? payload
            : new TextDecoder('utf-8').decode(payload);
          const data = JSON.parse(text);
          if (topic === 'budlee.voice.lesson' || data?.type === 'voice_lesson_control') {
            onLessonEvent?.(data);
            onToolCall?.({ name: data?.action || 'voice_lesson_control', arguments: data });
            onEvent?.({ type: 'voice_lesson_control', data });
            return;
          }
          if (topic === 'budlee.voice.status' || data?.type === 'voice_error') {
            const message = data?.message || 'Voice tutor could not start.';
            setError(message);
            setState('error');
            cleanup();
            setIsOpen(true);
            onEvent?.({ type: 'voice_error', data });
          }
        } catch (err) {
          console.error('Error handling LiveKit data message:', err);
        }
      });

      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        try {
          const isLocalSpeaking = speakers.some(p => p === room.localParticipant);
          const isRemoteSpeaking = speakers.some(p => p !== room.localParticipant);

          if (isRemoteSpeaking) {
            if (thinkingTimeoutRef.current) {
              clearTimeout(thinkingTimeoutRef.current);
              thinkingTimeoutRef.current = null;
            }
            setState('speaking');
            // Retain assistantText while tutor is speaking; subtitles are updated by TranscriptionReceived
          } else if (isLocalSpeaking) {
            if (thinkingTimeoutRef.current) {
              clearTimeout(thinkingTimeoutRef.current);
              thinkingTimeoutRef.current = null;
            }
            // Only switch to listening if remote is NOT speaking (prevent speaker bleed from triggering listening state)
            setState('listening');
          } else {
            // When user finishes speaking, immediately transition to 'thinking' until the tutor begins speaking
            setState((prev) => {
              if (prev === 'listening') {
                if (thinkingTimeoutRef.current) {
                  clearTimeout(thinkingTimeoutRef.current);
                }
                thinkingTimeoutRef.current = setTimeout(() => {
                  setState((curr) => (curr === 'thinking' ? 'live' : curr));
                  thinkingTimeoutRef.current = null;
                }, 4500);
                return 'thinking';
              }
              return prev === 'thinking' ? 'thinking' : 'live';
            });
          }
        } catch (err) {
          console.error('Error handling active speakers changed:', err);
        }
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        try {
          releaseScreenWakeLock('ai-tutor');
          if (userInitiatedEndRef.current) {
            setState('idle');
            cleanup();
            setIsOpen(false);
          } else if (activeRef.current) {
            console.warn(`LiveKit room disconnected unexpectedly (reason: ${reason}). Attempting seamless reconnection...`);
            if (reconnectAttemptsRef.current < 2) {
              reconnectAttemptsRef.current += 1;
              setState('reconnecting');
              setTimeout(() => {
                if (activeRef.current && !userInitiatedEndRef.current) {
                  cleanup(true);
                  start(lastCustomRoomNameRef.current || roomName, true).catch((e) => {
                    console.error('Auto-reconnection failed:', e);
                    setError('Connection to tutor was interrupted.');
                    setState('error');
                  });
                }
              }, 1200);
            } else {
              setError('Connection lost. Please tap Try Again to reconnect.');
              setState('error');
            }
          }
        } catch (err) {
          console.error('Error handling room disconnection:', err);
        }
      });

      room.on(RoomEvent.Reconnecting, () => {
        console.warn('LiveKit room is reconnecting...');
        setState('reconnecting');
      });

      room.on(RoomEvent.Reconnected, () => {
        console.info('LiveKit room reconnected.');
        reconnectAttemptsRef.current = 0;
        setState('live');
        acquireScreenWakeLock('ai-tutor');
      });

      // 4. Connect to Room
      const livekitUrl = url || 'ws://localhost:7880';
      const connectOptions = {
        autoSubscribe: true,
      };
      if (voice_transport?.mode === 'turn-tls') {
        connectOptions.rtcConfig = { iceTransportPolicy: 'relay' };
      }
      await room.connect(livekitUrl, token, connectOptions);

      // 5. Enable Local Microphone with noise suppression & echo cancellation
      if (typeof window !== 'undefined' && window.isSecureContext === false) {
        throw new Error('Microphone access requires HTTPS or localhost. When testing on a mobile device, please open via HTTPS (e.g. https://' + window.location.host + ') or connect via localhost.');
      }
      await room.localParticipant.setMicrophoneEnabled(true, AUDIO_CAPTURE_DEFAULTS);
      acquireScreenWakeLock('ai-tutor');

      // Check if tutor track is already subscribed; if not, maintain connecting state until remote track arrives
      const hasRemoteAudio = Array.from(room.remoteParticipants?.values() || []).some((p) =>
        Array.from(p.trackPublications?.values() || []).some((pub) => pub.isSubscribed && pub.kind === 'audio')
      );
      if (hasRemoteAudio) {
        setState('live');
      } else {
        setTimeout(() => {
          if (activeRef.current && roomRef.current?.state === 'connected') {
            setState((curr) => (curr === 'connecting' ? 'live' : curr));
          }
        }, 3000);
      }
    } catch (err) {
      cleanup();
      let errorMessage = err?.message || 'Could not start realtime voice.';
      if (
        errorMessage.includes('navigator.mediaDevices') ||
        errorMessage.includes('getUserMedia') ||
        (typeof window !== 'undefined' && window.isSecureContext === false)
      ) {
        const host = typeof window !== 'undefined' ? window.location.host : '';
        errorMessage = 'Microphone access requires HTTPS or localhost. When testing on a mobile device, please open via HTTPS (e.g. https://' + host + ') or connect via localhost.';
      }
      setError(errorMessage);
      setState('error');
      setIsOpen(true);
    }
  }, [cleanup, context, onEvent, onLessonEvent, onToolCall]);

  const stop = useCallback(() => {
    userInitiatedEndRef.current = true;
    reconnectAttemptsRef.current = 0;
    cleanup();
    setState('idle');
    setIsOpen(false);
  }, [cleanup]);

  const retry = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    userInitiatedEndRef.current = false;
    cleanup(true);
    return start(lastCustomRoomNameRef.current || roomName, false);
  }, [cleanup, roomName, start]);

  const toggleMute = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const enabled = room.localParticipant.isMicrophoneEnabled;
    room.localParticipant.setMicrophoneEnabled(!enabled, AUDIO_CAPTURE_DEFAULTS);
    setIsMuted(enabled);
  }, []);

  const publishData = useCallback(async (payload, topic = 'budlee.voice.lesson') => {
    const room = roomRef.current;
    if (!room || !activeRef.current) return false;
    try {
      const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
      const encoder = new TextEncoder();
      await room.localParticipant.publishData(encoder.encode(text), {
        reliable: true,
        topic,
      });
      return true;
    } catch (err) {
      console.warn('Failed to publish data to LiveKit room:', err);
      return false;
    }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  return {
    state,
    error,
    transcript,
    assistantText,
    messages,
    isOpen,
    start,
    stop,
    retry,
    isMuted,
    toggleMute,
    publishData,
    roomName,
  };
};
